import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import styled, { keyframes, createGlobalStyle } from 'styled-components';
import realatixicon from '../images/Realatix_Icon.png';
/* ============ GLOBAL STYLE ============ */
const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;700&display=swap');

  @keyframes loadingBar {
    0%   { width: 0%; }
    10%  { width: 8%; }
    30%  { width: 35%; }
    60%  { width: 62%; }
    85%  { width: 85%; }
    100% { width: 100%; }
  }

  @keyframes fadeInUp {
    from { opacity: 0; transform: translateY(30px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  @keyframes fadeIn {
    from { opacity: 0; }
    to   { opacity: 1; }
  }

  @keyframes dotPulse {
    0%, 80%, 100% { opacity: 0.2; transform: scale(0.8); }
    40%            { opacity: 1;   transform: scale(1); }
  }
`;

/* ============ KEYFRAMES ============ */
const subtlePulse = keyframes`
  0%   { box-shadow: 0 0 0 0 rgba(255,255,255,0.08); }
  70%  { box-shadow: 0 0 0 16px rgba(255,255,255,0); }
  100% { box-shadow: 0 0 0 0 rgba(255,255,255,0); }
`;

const shimmer = keyframes`
  0%   { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

/* ============ STYLED COMPONENTS ============ */

const Wrapper = styled.div`
  position: fixed;
  inset: 0;
  background: radial-gradient(ellipse at 60% 40%, #1a3a6e 0%, #0d2254 40%, #071540 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-family: 'DM Sans', sans-serif;
  overflow: hidden;
`;

/* Glass icon badge */
const IconBadge = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 2rem;
  animation: fadeInUp 0.7s cubic-bezier(0.22, 1, 0.36, 1) 0.2s both, ${subtlePulse} 3s ease-out 1s infinite;
`;

/* Title */
const Title = styled.h1`
  margin: 0 0 0.5rem;
  font-size: ${(p) => (p.small ? '2.2rem' : '3.5rem')};
  font-weight: 700;
  letter-spacing: -0.5px;
  line-height: 1;
  animation: fadeInUp 0.7s cubic-bezier(0.22, 1, 0.36, 1) 0.35s both;

  .bold { color: #ffffff; font-weight: 700; }
  .light { color: rgba(255,255,255,0.45); font-weight: 300; }
`;

/* Thin divider */
const Divider = styled.div`
  width: 40px;
  height: 1px;
  background: rgba(255,255,255,0.3);
  margin: 1.25rem auto;
  animation: fadeIn 0.6s ease 0.55s both;
`;

/* Tagline */
const Tagline = styled.p`
  margin: 0;
  font-size: ${(p) => (p.small ? '0.7rem' : '0.78rem')};
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: rgba(255,255,255,0.5);
  font-weight: 400;
  animation: fadeInUp 0.7s cubic-bezier(0.22, 1, 0.36, 1) 0.55s both;
`;

/* Loading label */
const LoadingLabel = styled.p`
  margin: 0 0 0.75rem;
  font-size: 0.68rem;
  letter-spacing: 0.25em;
  text-transform: uppercase;
  color: rgba(255,255,255,0.35);
  animation: fadeIn 0.6s ease 0.75s both;
`;

/* Dots */
const DotsRow = styled.div`
  display: flex;
    justify-content: center;   /* ← أضف السطر ده */

  gap: 6px;
  margin-bottom: 1.25rem;
  animation: fadeIn 0.6s ease 0.8s both;
`;

const Dot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: rgba(255,255,255,0.5);
  animation: dotPulse 1.4s ease-in-out infinite;
  animation-delay: ${(p) => p.delay};
`;

/* Progress bar track */
const BarTrack = styled.div`
  width: ${(p) => (p.small ? '240px' : '340px')};
  height: 3px;
  background: rgba(255,255,255,0.1);
  border-radius: 999px;
  overflow: hidden;
  animation: fadeIn 0.6s ease 0.85s both;
`;

/* Progress bar fill */
const BarFill = styled.div`
  height: 100%;
  width: 0%;
  border-radius: 999px;
  background: linear-gradient(90deg, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0.8) 50%, rgba(255,255,255,0.3) 100%);
  background-size: 400px 100%;
  animation:
    loadingBar ${(p) => p.duration}s cubic-bezier(0.4, 0, 0.2, 1) forwards,
    ${shimmer} 1.5s linear infinite;
`;

/* Version tag */
const VersionTag = styled.p`
  position: absolute;
  bottom: 2.5rem;
  font-size: 0.62rem;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: rgba(255,255,255,0.2);
  animation: fadeIn 1s ease 1.2s both;
`;

/* Bottom progress bar (very bottom of screen) */
const BottomBar = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  height: 2px;
  background: rgba(255,255,255,0.15);
  animation: loadingBar ${(p) => p.duration}s cubic-bezier(0.4, 0, 0.2, 1) forwards;
`;

/* ── Welcome screen (after splash) ── */
const WelcomeTitle = styled.h1`
  font-size: ${(p) => (p.small ? '1.8rem' : '2.6rem')};
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 2.5rem;
  animation: fadeInUp 0.8s cubic-bezier(0.22, 1, 0.36, 1) both;
  letter-spacing: -0.3px;

  span {
    color: rgba(255,255,255,0.45);
    font-weight: 300;
  }
`;

const GetStartedBtn = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.85rem 2rem;
  border-radius: 10px;
  background: rgba(255,255,255,0.12);
  border: 1px solid rgba(255,255,255,0.25);
  backdrop-filter: blur(8px);
  color: #ffffff;
  font-family: 'DM Sans', sans-serif;
  font-size: 0.9rem;
  font-weight: 500;
  letter-spacing: 0.05em;
  text-decoration: none;
  text-transform: uppercase;
  transition: background 0.2s, transform 0.2s, box-shadow 0.2s;
  animation: fadeInUp 0.8s cubic-bezier(0.22, 1, 0.36, 1) 0.15s both;

  &:hover {
    background: rgba(255,255,255,0.2);
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0,0,0,0.2);
    color: #fff;
  }

  svg {
    transition: transform 0.2s;
  }

  &:hover svg {
    transform: translateX(4px);
  }
`;

/* Building icon SVG */
const BuildingIcon = () => (
  <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="4" y="6" width="12" height="24" rx="1" stroke="white" strokeWidth="1.5" fill="none" opacity="0.7"/>
    <rect x="20" y="12" width="12" height="18" rx="1" stroke="white" strokeWidth="1.5" fill="none" opacity="0.7"/>
    <line x1="7" y1="11" x2="10" y2="11" stroke="white" strokeWidth="1.5" strokeLinecap="round" opacity="0.6"/>
    <line x1="13" y1="11" x2="16" y2="11" stroke="white" strokeWidth="1.5" strokeLinecap="round" opacity="0.6"/>
    <line x1="7" y1="16" x2="10" y2="16" stroke="white" strokeWidth="1.5" strokeLinecap="round" opacity="0.6"/>
    <line x1="13" y1="16" x2="16" y2="16" stroke="white" strokeWidth="1.5" strokeLinecap="round" opacity="0.6"/>
    <line x1="7" y1="21" x2="10" y2="21" stroke="white" strokeWidth="1.5" strokeLinecap="round" opacity="0.6"/>
    <line x1="13" y1="21" x2="16" y2="21" stroke="white" strokeWidth="1.5" strokeLinecap="round" opacity="0.6"/>
    <line x1="23" y1="17" x2="26" y2="17" stroke="white" strokeWidth="1.5" strokeLinecap="round" opacity="0.6"/>
    <line x1="29" y1="17" x2="32" y2="17" stroke="white" strokeWidth="1.5" strokeLinecap="round" opacity="0.6"/>
    <line x1="23" y1="22" x2="26" y2="22" stroke="white" strokeWidth="1.5" strokeLinecap="round" opacity="0.6"/>
    <line x1="29" y1="22" x2="32" y2="22" stroke="white" strokeWidth="1.5" strokeLinecap="round" opacity="0.6"/>
  </svg>
);

/* ============ COMPONENT ============ */
export const HomePage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [showWelcome, setShowWelcome] = useState(false);
  const SPLASH_DURATION = 5; // seconds

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowWelcome(true);
    }, SPLASH_DURATION * 1000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      <GlobalStyle />
      <Wrapper>
        {!showWelcome ? (
          /* ======= SPLASH SCREEN ======= */
          <>
            <IconBadge>
              {/* <BuildingIcon /> */}
              <img src={realatixicon} alt='logo'  style={{width:'110px',height:'90px'}}/>
            </IconBadge>

            <Title small={isMobile}>
              <span className="bold">Realatix </span>
              <span className="light">CRM</span>
            </Title>

            <Divider />

            <Tagline small={isMobile}>
Manage your customers and sales easily, quickly, and securely with our CRM.

            </Tagline>

            <div style={{ marginTop: '2.5rem', textAlign: 'center' }}>
              <LoadingLabel>Core system is ready – secure, fast, and stable.</LoadingLabel>

              <DotsRow>
                <Dot delay="0s" />
                <Dot delay="0.2s" />
                <Dot delay="0.4s" />
              </DotsRow>

              <BarTrack small={isMobile}>
                <BarFill duration={SPLASH_DURATION} />
              </BarTrack>
            </div>

            <VersionTag>V 2 . 0 . 4 &nbsp; E X E C U T I V E &nbsp; T I E R</VersionTag>

            <BottomBar duration={SPLASH_DURATION} />
          </>
        ) : (
          /* ======= WELCOME SCREEN ======= */
          <div style={{ textAlign: 'center', padding: '1rem' }}>
            <IconBadge style={{ margin: '0 auto 1.5rem' }}>
              {/* <BuildingIcon /> */}
                            <img src={realatixicon} alt='logo'  style={{width:'110px',height:'90px'}}/>

            </IconBadge>

            <WelcomeTitle small={isMobile}>
              Welcome to Realatix <span>CRM</span>
            </WelcomeTitle>

            <GetStartedBtn to="/Login">
              Get Started
              <svg height="18" width="18" viewBox="0 0 24 24" fill="none">
                <path
                  d="M16.172 11l-5.364-5.364 1.414-1.414L20 12l-7.778 7.778-1.414-1.414L16.172 13H4v-2z"
                  fill="currentColor"
                />
              </svg>
            </GetStartedBtn>
          </div>
        )}
      </Wrapper>
    </>
  );
};
